import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Header from "../../components/Header";
import { base_url, getToken } from "../../config/config";

const getRecordEndpoints = () => {
  const normalizeBase = (value) => String(value || "").replace(/\/+$/, "");
  const locationOrigin =
    typeof window !== "undefined" ? normalizeBase(window.location.origin) : "";
  const configuredBase = normalizeBase(base_url);

  const bases = [configuredBase, locationOrigin, ""].filter(
    (value, index, array) => value || index === array.length - 1,
  );

  const paths = [
    "/v1/getLoginLogoutRecords",
    "/getLoginLogoutRecords",
    "/v1/management-log/logs",
  ];

  const endpoints = [];
  for (const base of bases) {
    for (const path of paths) {
      endpoints.push(base ? `${base}${path}` : path);
    }
  }

  return Array.from(new Set(endpoints));
};

const normalizeKey = (record) => {
  if (record?.userId) {
    return `id:${record.userId}`;
  }
  if (record?.email) {
    return `email:${String(record.email).trim().toLowerCase()}`;
  }
  return `name:${String(record?.userName || "unknown")
    .trim()
    .toLowerCase()}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

const matchesUserName = (record, term) =>
  String(record?.userName || "")
    .toLowerCase()
    .includes(term);

const LoggedInUsers = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [showActiveUsersOnly, setShowActiveUsersOnly] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError("");

    const token = getToken();
    const endpoints = getRecordEndpoints();

    for (const endpoint of endpoints) {
      try {
        const url = endpoint.startsWith("http")
          ? new URL(endpoint)
          : new URL(endpoint, window.location.origin);
        url.searchParams.set("page", "1");
        url.searchParams.set("limit", "1000");

        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.status === 404) {
          continue;
        }

        const result = await response
          .json()
          .catch(() => ({ message: "Invalid JSON response from server." }));

        if (!response.ok) {
          throw new Error(result?.message || "Failed to fetch login records.");
        }

        const data = Array.isArray(result?.data) ? result.data : [];
        setRecords(data);
        setLoading(false);
        return;
      } catch (fetchError) {
        setError(fetchError?.message || "Failed to fetch login records.");
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const orderedRecords = useMemo(
    () =>
      [...records].sort(
        (a, b) =>
          new Date(b?.recordAt || b?.createdAt || 0).getTime() -
          new Date(a?.recordAt || a?.createdAt || 0).getTime(),
      ),
    [records],
  );

  const activeUsers = useMemo(() => {
    const latestByUser = new Map();
    for (const record of orderedRecords) {
      const key = normalizeKey(record);
      if (!latestByUser.has(key)) {
        latestByUser.set(key, record);
      }
    }

    const onlyLoggedIn = Array.from(latestByUser.values()).filter(
      (record) => String(record?.action || "").toLowerCase() === "login",
    );

    const term = search.trim().toLowerCase();
    if (!term) {
      return onlyLoggedIn;
    }

    return onlyLoggedIn.filter((record) => matchesUserName(record, term));
  }, [orderedRecords, search]);

  const activeUserKeys = useMemo(
    () => new Set(activeUsers.map((record) => normalizeKey(record))),
    [activeUsers],
  );

  const historyRecords = useMemo(() => {
    const term = search.trim().toLowerCase();

    let filtered = orderedRecords;

    if (showActiveUsersOnly) {
      filtered = filtered.filter((record) =>
        activeUserKeys.has(normalizeKey(record)),
      );
    }

    if (!term) {
      return filtered;
    }

    return filtered.filter((record) => matchesUserName(record, term));
  }, [orderedRecords, search, showActiveUsersOnly, activeUserKeys]);

  return (
    <Box m="20px">
      <Header
        title="Logged In Users"
        subtitle="Users currently considered logged in based on latest login/logout events"
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          gap={2}
          flexDirection={{ xs: "column", md: "row" }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="h6">
              {activeTab === 0 ? "Active Users" : "All Login/Logout Events"}
            </Typography>
            <Chip
              color={activeTab === 0 ? "success" : "info"}
              label={
                activeTab === 0
                  ? `${activeUsers.length} online`
                  : `${historyRecords.length} records`
              }
              size="small"
            />
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            <TextField
              size="small"
              label="Search by user name"
              placeholder="e.g. Abdul Wasay Ahmed"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {activeTab === 1 ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={showActiveUsersOnly}
                    onChange={(event) =>
                      setShowActiveUsersOnly(event.target.checked)
                    }
                  />
                }
                label="Show active users only"
              />
            ) : null}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchRecords}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ mt: 2 }}
        >
          <Tab label="Active Users" />
          <Tab label="All Events" />
        </Tabs>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {activeTab === 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>User Type</TableCell>
                <TableCell>Logged In At</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>IP Address</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={22} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : activeUsers.length ? (
                activeUsers.map((record) => (
                  <TableRow key={record?._id || normalizeKey(record)} hover>
                    <TableCell>{record?.userName || "-"}</TableCell>
                    <TableCell>{record?.email || "-"}</TableCell>
                    <TableCell>{record?.userModel || "-"}</TableCell>
                    <TableCell>{formatDateTime(record?.recordAt)}</TableCell>
                    <TableCell>{record?.source || "web"}</TableCell>
                    <TableCell>{record?.ipAddress || "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box py={2} textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        No currently logged-in users found.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>User Type</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Record At</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>IP Address</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={22} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : historyRecords.length ? (
                historyRecords.map((record) => (
                  <TableRow
                    key={
                      record?._id ||
                      `${normalizeKey(record)}-${record?.recordAt || ""}`
                    }
                    hover
                  >
                    <TableCell>{record?.userName || "-"}</TableCell>
                    <TableCell>{record?.email || "-"}</TableCell>
                    <TableCell>{record?.userModel || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={
                          String(record?.action || "").toLowerCase() === "login"
                            ? "success"
                            : "warning"
                        }
                        label={String(record?.action || "-").toUpperCase()}
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(record?.recordAt)}</TableCell>
                    <TableCell>{record?.source || "web"}</TableCell>
                    <TableCell>{record?.ipAddress || "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box py={2} textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        No login/logout history found.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default LoggedInUsers;
